import {
    Link,
    Outlet,
    createFileRoute,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router'
import {
    LogOut,
    Menu,
    PanelLeftClose,
    PanelLeftOpen,
    QrCode,
    Store,
    X,
    Zap,
} from 'lucide-react'
import { useState } from 'react'

import { AppSidebar } from '@/components/sidebar/app-sidebar'
import { BuildVersionBadge } from '@/components/sidebar/build-version-badge'
import { getNavItems } from '@/components/sidebar/nav-items'
import { filterNavEntries } from '@/components/sidebar/nav-filter'
import { buildSidebar } from '@/components/sidebar/nav-layout'
import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { ActiveTripBadge } from '@/components/delivery/active-trip-badge'
import { EInvoiceReminderTrigger } from '@/components/einvoice-reminder/einvoice-reminder-trigger'
import { MyGrowerQRTrigger } from '@/components/growers/my-grower-qr-trigger'
import { NotificationDropdown } from '@/components/notifications/notification-dropdown'
import { WhatsNewTrigger } from '@/components/changelog/whats-new-trigger'
import { PwaInstallButton } from '@/components/pwa/pwa-install-button'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { StagingTestBadge } from '@/components/staging-test-badge'
import { SupportReportTrigger } from '@/components/support/support-report-trigger'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useMinWidth } from '@/hooks/use-min-width'
import { useSidebarCollapsed } from '@/hooks/use-sidebar-collapsed'
import { useSidebarLayout } from '@/hooks/use-sidebar-layout-query'
import { APP_NAME } from '@/lib/constants'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar')({
    component: AppLayout,
})

function AppLayout() {
    const { user, logout, hasPermission, shopEnabled, zaloOAEnabled } =
        useAuth()
    // Drivers get delivery_trips.read.all (to reach the Delivery Trips menu +
    // API) yet keep shipping_carriers.read (order-form carrier picker). Neither
    // permission can tell "is a driver" apart, so hideForDriver menu entries are
    // resolved by dashboard role instead.
    const dashboardRole = useDashboardRole({ allowKioskRedirect: false })
    const isDriver =
        dashboardRole.kind === 'dashboard' &&
        dashboardRole.dashboard === 'driver'
    // Tài khoản hộ trồng: header có thêm nút QR số điện thoại của hộ mình.
    const isGrower =
        dashboardRole.kind === 'dashboard' &&
        dashboardRole.dashboard === 'grower'
    const kiotvietOrdersEnabled = useFeatureFlag('dev-kiotviet-orders')
    const { data: sidebarLayout } = useSidebarLayout()
    // Known flag states for nav filtering. A flagged entry whose key is
    // missing here would be hidden (fail-safe), so register every nav flag.
    const navFlagOn: Record<string, boolean | string> = {
        'dev-kiotviet-orders': kiotvietOrdersEnabled,
        // SHOP_ENABLED của bản deploy (/auth/me) — menu Bán lẻ + 2 báo cáo bán online.
        'server:shop': shopEnabled === true,
        // ZALO_OA_ENABLED của bản deploy (/auth/me) — Quản trị → Zalo ZBS.
        'server:zalo-oa': zaloOAEnabled === true,
    }
    const flagAllows = (flag?: string) => !flag || Boolean(navFlagOn[flag])
    const navigate = useNavigate()
    const routerState = useRouterState()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [qrOpen, setQrOpen] = useState(false)
    // Từ `xl` thanh bên đứng cố định và có hai dạng: mở rộng / thu gọn (chỉ
    // icon, 50px). Dưới `xl` nó là ngăn kéo nên luôn vẽ dạng mở rộng.
    const isDesktop = useMinWidth(1280)
    const { collapsed: collapsedPref, toggle: toggleCollapsed } =
        useSidebarCollapsed()
    const collapsed = isDesktop && collapsedPref

    const currentPath = routerState.location.pathname
    // The tracking hub is a full-bleed map + camera wall that manages its own
    // insets, so it drops the shell's desktop page padding.
    const fullBleed = currentPath === '/tracking'

    // beforeLoad on _authed guarantees user exists, but useQuery may lag one render cycle
    if (!user) return null

    const handleLogout = async () => {
        await logout()
        void navigate({ to: '/login', search: { redirect: undefined } })
    }

    // Feature-flag + permission gate (both levels, incl. anyPermission via
    // entryAllowsPermission), plus hideForDriver entries for driver accounts.
    // See filterNavEntries.
    const navItems = filterNavEntries(getNavItems(), {
        hasPermission,
        flagAllows,
        isDriver,
    })

    // Dựng cấu trúc theo cấu hình admin (tạo cha, re-parent con, đổi tên) trên
    // tập entries ĐÃ lọc quyền/flag ở trên → giữ nguyên bảo mật (con không được
    // phép xem đã bị loại trước khi dựng). Sau khi dựng, ẩn group rỗng (con đã
    // dời hết đi hoặc user không có quyền). Layout rỗng/chưa load → mặc định.
    const toggleLabel = isDesktop
        ? collapsed
            ? m.nav_sidebar_expand()
            : m.nav_sidebar_collapse()
        : sidebarOpen
          ? m.nav_sidebar_close()
          : m.nav_sidebar_open()

    const orderedNavItems = buildSidebar(
        navItems,
        sidebarLayout?.layout,
    ).filter((entry) => !('items' in entry) || entry.items.length > 0)

    return (
        // Desktop: khoá shell = chiều cao viewport + ẩn scroll body → header và
        // sidebar đứng yên, chỉ <main> (overflow-auto) và nav sidebar
        // (overflow-y-auto) tự cuộn. Mobile giữ nguyên (min-h-screen, scroll body).
        <div className="min-h-screen flex bg-background lg:h-screen lg:overflow-hidden">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-20 bg-black/40 xl:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar
                Chỉ đứng cố định từ `xl` (1280px) trở lên. Dưới ngưỡng đó nó là
                ngăn kéo trượt ra khi bấm ☰ — iPad xoay ngang rộng 1180px, để
                thanh bên chiếm cứng 224px thì các bảng sổ (rộng hơn 1000px) chỉ
                còn hơn 800px và phải cuộn ngang liên tục. */}
            <aside
                // h-dvh chứ không h-full: với phần tử `fixed`, chiều cao phần trăm
                // tính theo viewport LỚN, nên trên Chrome Android đáy thanh bên
                // (chỗ nút Đăng xuất) nằm khuất dưới thanh địa chỉ và không bấm
                // được — cuộn cũng không ra vì phần cuộn là danh sách menu bên
                // trong, không phải trang. dvh trừ đúng phần thanh đang hiện.
                className={`fixed top-0 left-0 z-30 h-dvh bg-card border-r flex flex-col transition-[transform,width] duration-200 xl:static xl:h-full xl:translate-x-0 ${
                    collapsed ? 'w-[50px]' : 'w-56'
                } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
                <div className="h-14 flex items-center justify-center border-b shrink-0">
                    <Link
                        to="/"
                        onClick={() => setSidebarOpen(false)}
                        aria-label={APP_NAME}
                        className="relative flex items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <img
                            src="/logo.jpg"
                            alt={APP_NAME}
                            className={
                                collapsed
                                    ? 'w-10 h-auto object-contain'
                                    : 'h-11 w-auto object-contain'
                            }
                        />
                        <StagingTestBadge size={collapsed ? 'sm' : 'md'} />
                    </Link>
                </div>

                {/* Nav links */}
                <AppSidebar
                    entries={orderedNavItems}
                    currentPath={currentPath}
                    onNavigate={() => setSidebarOpen(false)}
                    collapsed={collapsed}
                />
                {!collapsed && (
                    <div className="shrink-0 px-4 pb-2">
                        <BuildVersionBadge />
                    </div>
                )}

                {/* User section — thu gọn thì chỉ còn nút đăng xuất dạng icon */}
                {collapsed ? (
                    <div className="border-t p-1.5 shrink-0">
                        <Tooltip content={m.auth_logout()} placement="right">
                            <button
                                onClick={handleLogout}
                                aria-label={m.auth_logout()}
                                className="flex h-10 w-full items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                            >
                                <LogOut className="size-4 shrink-0" />
                            </button>
                        </Tooltip>
                    </div>
                ) : (
                    <div className="border-t p-3 shrink-0">
                        <div className="text-xs text-muted-foreground truncate mb-2 px-1">
                            {user.username}
                        </div>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors w-full"
                        >
                            <LogOut className="size-4 shrink-0" />
                            {m.auth_logout()}
                        </button>
                    </div>
                )}
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-10 h-14 border-b flex items-center px-4 gap-3 shrink-0 bg-card lg:static lg:z-auto">
                    {/* Desktop: thu gọn / mở rộng thanh bên. Dưới xl: mở ngăn kéo. */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-10"
                        aria-label={toggleLabel}
                        title={toggleLabel}
                        onClick={
                            isDesktop
                                ? toggleCollapsed
                                : () => setSidebarOpen((o) => !o)
                        }
                    >
                        {isDesktop ? (
                            collapsed ? (
                                <PanelLeftOpen className="size-6" />
                            ) : (
                                <PanelLeftClose className="size-6" />
                            )
                        ) : sidebarOpen ? (
                            <X className="size-6" />
                        ) : (
                            <Menu className="size-6" />
                        )}
                    </Button>
                    <span className="flex-1 min-w-0 flex items-center gap-2 xl:hidden">
                        {/* Còn chỗ thì logo 24px + ruy băng TEST; header chật (vai trò
                            nhiều nút, ≤ ~393px) thì co như cũ, không tràn ngang. */}
                        <span className="relative inline-flex size-6 min-w-0 shrink">
                            <img
                                src="/logo.jpg"
                                alt={APP_NAME}
                                className="size-6"
                            />
                            <StagingTestBadge size="sm" />
                        </span>
                    </span>
                    <div className="flex-1 hidden xl:block" />
                    {hasPermission('sales_orders.pickup_order') && (
                        <ActiveTripBadge />
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-10 sm:size-8"
                        onClick={() => setQrOpen(true)}
                        title={m.nav_scan_qr()}
                    >
                        <QrCode className="size-5 sm:size-4" />
                    </Button>
                    {isGrower && <MyGrowerQRTrigger />}
                    <PwaInstallButton />
                    <SupportReportTrigger />
                    <WhatsNewTrigger />
                    <EInvoiceReminderTrigger />
                    <NotificationDropdown align="right" />
                    {hasPermission('sales_orders.create') && (
                        <Link
                            to="/orders/quick"
                            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white hover:bg-amber-700 transition-colors sm:px-2.5 sm:py-1.5"
                            title={m.order_quick_title()}
                        >
                            <Zap className="size-5 sm:size-4" />
                            <span className="hidden lg:inline">
                                {m.order_quick_title()}
                            </span>
                        </Link>
                    )}
                    {hasPermission('sales_orders.quick_create') && (
                        <Link
                            to="/sale"
                            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors sm:px-2.5 sm:py-1.5"
                            title={m.nav_sales()}
                        >
                            <Store className="size-5 sm:size-4" />
                            <span className="hidden lg:inline">
                                {m.nav_sales()}
                            </span>
                        </Link>
                    )}
                </header>

                <main
                    className={`flex-1 overflow-auto p-0 ${
                        fullBleed ? '' : 'lg:p-3'
                    }`}
                >
                    <Outlet />
                </main>
            </div>

            <QRScannerDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                mode="navigate"
            />
        </div>
    )
}
